import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { getPlaywrightTestSetup } from '../../features/utils/PlaywrightTestSetup';

type TelemetryPropertyValue = string | number | boolean | null | undefined;
type TelemetryProperties = Record<string, string>;
type TelemetryPropertiesInput = Record<string, TelemetryPropertyValue>;

let appInsightsInstance: ApplicationInsights | null = null;
let telemetryInitialized = false;

const toError = (error: unknown): Error => {
	if (error instanceof Error) {
		return error;
	}

	return new Error(typeof error === 'string' ? error : JSON.stringify(error));
};

const toTelemetryProperties = (properties?: TelemetryPropertiesInput): TelemetryProperties | undefined => {
	if (!properties) {
		return undefined;
	}

	const entries = Object.entries(properties).map(([key, value]) => [
		key,
		value === null || value === undefined ? '' : String(value),
	]);

	return Object.fromEntries(entries);
};

const telemetryInitializer = (envelope: ITelemetryItem): boolean => {
	// Remove AI-generated session IDs from telemetry; we set user context explicitly after auth.
	if (envelope.tags) {
		envelope.tags['ai.session.id'] = ' ';
	}

	return true;
};

const wireGlobalErrorHandlers = (): void => {
	window.addEventListener('error', (event) => {
		trackException(event.error ?? event.message, {
			source: 'window.onerror',
			filename: event.filename ?? '',
			lineno: String(event.lineno ?? ''),
			colno: String(event.colno ?? ''),
		});
	});

	window.addEventListener('unhandledrejection', (event) => {
		trackException(event.reason, {
			source: 'window.unhandledrejection',
		});
	});
};

export const initializeAppInsights = (): void => {
	if (telemetryInitialized) {
		return;
	}

	if (getPlaywrightTestSetup()) {
		return;
	}

	telemetryInitialized = true;

	const connectionString = process.env.REACT_APP_INSIGHTS_CONNECTION_STRING!;

	if (!connectionString || connectionString === 'undefined' || connectionString === 'null') {
		return;
	}

	appInsightsInstance = new ApplicationInsights({
		config: {
			connectionString,
			disableCookiesUsage: true,
			autoTrackPageVisitTime: false,
			enableAutoRouteTracking: true,
			disableExceptionTracking: false,
			disableAjaxTracking: true,
			disableFetchTracking: true,
			disableCorrelationHeaders: true,
		},
	});

	appInsightsInstance.loadAppInsights();
	appInsightsInstance.addTelemetryInitializer(telemetryInitializer);

	wireGlobalErrorHandlers();
};

export const trackException = (error: unknown, properties?: TelemetryProperties): void => {
	if (!appInsightsInstance) {
		return;
	}

	try {
		appInsightsInstance.trackException({
			exception: toError(error),
			properties,
		});
	} catch {
		// Intentionally swallow telemetry failures so user flows are never blocked.
	}
};

export const trackServiceException = (
	error: unknown,
	context: {
		source: string;
		operation?: string;
		endpoint?: string;
		providerType?: string;
		sessionId?: string;
		statusCode?: number;
		retryAttempt?: number;
		isFatal?: boolean;
	},
	properties?: TelemetryPropertiesInput,
): void => {
	trackException(error, {
		layer: 'service',
		source: context.source,
		operation: context.operation ?? '',
		endpoint: context.endpoint ?? '',
		providerType: context.providerType ?? '',
		sessionId: context.sessionId ?? '',
		statusCode: context.statusCode === undefined ? '' : String(context.statusCode),
		retryAttempt: context.retryAttempt === undefined ? '' : String(context.retryAttempt),
		isFatal: context.isFatal === undefined ? '' : String(context.isFatal),
		...toTelemetryProperties(properties),
	});
};

export const trackEvent = (name: string, properties?: TelemetryProperties): void => {
	if (!appInsightsInstance) {
		return;
	}

	try {
		appInsightsInstance.trackEvent({
			name,
			properties,
		});
	} catch {
		// Intentionally swallow telemetry failures so user flows are never blocked.
	}
};

export const setTelemetryUserContext = (authenticatedUserId: string, accountId?: string): void => {
	if (!appInsightsInstance) {
		return;
	}

	try {
		appInsightsInstance.setAuthenticatedUserContext(authenticatedUserId, accountId, false);
	} catch {
		// Intentionally swallow telemetry failures so user flows are never blocked.
	}
};

export const clearTelemetryUserContext = (): void => {
	if (!appInsightsInstance) {
		return;
	}

	try {
		appInsightsInstance.clearAuthenticatedUserContext();
	} catch {
		// Intentionally swallow telemetry failures so user flows are never blocked.
	}
};
